import { inject } from 'vue'
import { RequestService } from './RequestService'

export const AdminCenterService = () => {
  const { simpleGet, post, put, del, handleErrors } = RequestService()
  const functionsBaseUrl = inject('functionsBaseUrl')

  /**
   * Fetches agency members from the backend with optional pagination.
   * @param {Object} [params]
   * @param {string} [params.type] - 'active' | 'pending' (omit for all)
   * @param {number} [params.limit] - Page size
   * @param {number} [params.offset] - Offset for pagination
   * @returns {Promise<Object>} Backend response data
   */
  async function getAgencyMembers(params = {}) {
    try {
      const query = new URLSearchParams()
      if (params.type) query.set('type', params.type)
      if (params.limit) query.set('limit', String(params.limit))
      if (params.offset) query.set('offset', String(params.offset))
      const qs = query.toString()
      const url = `${functionsBaseUrl}/usersGateway/api/v1/users/agency-members${qs ? '?' + qs : ''}`

      const response = await simpleGet('getAgencyMembers', url)

      if (response?.data?.success) {
        return response.data.data
      }

      throw new Error(response?.data?.message || 'Failed to load agency members')
    } catch (error) {
      handleErrors('getAgencyMembers', error)
      throw error
    }
  }

  /**
   * Creates a single user via the bulk endpoint (array of one).
   * @param {Object} params
   * @param {string} params.firstName
   * @param {string} params.lastName
   * @param {string} params.title
   * @param {string} params.email
   * @param {string} params.agencyId
   * @param {string[]} params.roleKeys - Firestore access.roleKeys for the new user
   * @returns {Promise<Object>} result { success, email, status?, error? }
   */
  async function createUser({ firstName, lastName, title, email, agencyId, roleKeys }) {
    try {
      const payload = [{
        'First Name': firstName,
        'Last Name': lastName,
        'Role/Title': title,
        Email: email,
        Agency: String(agencyId),
        roleKeys: roleKeys || []
      }]

      const resp = await post(
        'createUser',
        `${functionsBaseUrl}/usersGateway/api/v1/users`,
        payload
      )

      const results = resp.data.data
      if (!results || !results.length) {
        throw new Error('No response from server')
      }

      const result = results[0]
      if (!result.success) {
        const err = new Error(result.message || result.error || 'Failed to create user')
        err.code = result.error
        throw err
      }

      return result
    } catch (error) {
      handleErrors('createUser', error)
      throw error
    }
  }

  /**
   * Appends a role key to a user via PUT /api/v1/users/:uid/role.
   * @param {string} uid - User ID
   * @param {string} roleKey - Catalog role key to append (e.g. 'agencyAdmin')
   * @returns {Promise<Object>} result from backend
   */
  async function grantUserRoleKey(uid, roleKey) {
    try {
      const resp = await put(
        'grantUserRoleKey',
        `${functionsBaseUrl}/usersGateway/api/v1/users/${uid}/role`,
        { roleKey }
      )

      if (resp?.data?.success) {
        return resp.data.data
      }

      throw new Error(resp?.data?.message || 'Failed to grant user role')
    } catch (error) {
      handleErrors('grantUserRoleKey', error)
      throw error
    }
  }

  /**
   * Deactivates a user and optionally reassigns their employers.
   * @param {string} uid - User ID to deactivate
   * @param {string|null} reassignTo - Email of new owner, or null to unassign
   * @returns {Promise<Object>} result from backend
   */
  async function deactivateUser(uid, reassignTo = null) {
    try {
      const resp = await put(
        'deactivateUser',
        `${functionsBaseUrl}/usersGateway/api/v1/users/${uid}/deactivate`,
        { reassignTo: reassignTo || null }
      )

      if (resp?.data?.success) {
        return resp.data.data
      }

      throw new Error(resp?.data?.message || 'Failed to deactivate user')
    } catch (error) {
      handleErrors('deactivateUser', error)
      throw error
    }
  }

  /**
   * Resends the password creation invite email to a pending user.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function resendInvite(uid) {
    try {
      const resp = await put(
        'resendInvite',
        `${functionsBaseUrl}/usersGateway/api/v1/users/${uid}/resend-invite`,
        {}
      )

      if (resp?.data?.success) {
        return resp.data.data
      }

      throw new Error(resp?.data?.message || 'Failed to resend invite')
    } catch (error) {
      handleErrors('resendInvite', error)
      throw error
    }
  }

  /**
   * Cancels a pending invite by deleting the user entirely.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function cancelInvite(uid) {
    try {
      const resp = await del(
        'cancelInvite',
        `${functionsBaseUrl}/usersGateway/api/v1/users/${uid}/invite`
      )

      if (resp?.data?.success) {
        return resp.data.data
      }

      throw new Error(resp?.data?.message || 'Failed to cancel invite')
    } catch (error) {
      handleErrors('cancelInvite', error)
      throw error
    }
  }

  /**
   * Fetches employer plan years for the agency with assignment info.
   * @param {Object} [params]
   * @param {string} [params.type] - 'all' | 'unassigned'
   * @param {number} [params.limit] - Page size
   * @param {number} [params.offset] - Offset for pagination
   * @returns {Promise<Object>} { employers, totalAll, totalUnassigned }
   */
  async function getAgencyEmployers(params = {}) {
    try {
      const query = new URLSearchParams()
      if (params.type) query.set('type', params.type)
      if (params.limit) query.set('limit', String(params.limit))
      if (params.offset) query.set('offset', String(params.offset))
      const qs = query.toString()
      const url = `${functionsBaseUrl}/usersGateway/api/v1/users/agency-employers${qs ? '?' + qs : ''}`

      const response = await simpleGet('getAgencyEmployers', url)

      if (response?.data?.success) {
        return response.data.data
      }

      throw new Error(response?.data?.message || 'Failed to load agency employers')
    } catch (error) {
      handleErrors('getAgencyEmployers', error)
      throw error
    }
  }

  /**
   * Batch-updates the account owners on employer documents (full replacement, max 3).
   * @param {Array} assignments - [{ employerId, accountOwners, accountOwner }]
   * @returns {Promise<Object>}
   */
  async function updateEmployerAssignments(assignments) {
    try {
      const resp = await put(
        'updateEmployerAssignments',
        `${functionsBaseUrl}/usersGateway/api/v1/users/employer-assignments`,
        { assignments }
      )

      if (resp?.data?.success) {
        return resp.data.data
      }

      throw new Error(resp?.data?.message || 'Failed to update assignments')
    } catch (error) {
      handleErrors('updateEmployerAssignments', error)
      throw error
    }
  }

  /**
   * BCS Transform certification progress of the caller's agency learners, unsorted.
   * The agency is resolved server-side from the caller.
   * @returns {Promise<{courseId: string|null, rows: Array<Object>}>}
   */
  async function getTransformCertificationProgress() {
    try {
      // rethrow: the store shows the gateway's message, which only reaches it on the axios error.
      const response = await simpleGet(
        'getTransformCertificationProgress',
        `${functionsBaseUrl}/transformLearningGateway/api/v1/agency-certification-progress`,
        { rethrow: true }
      )

      if (response?.data?.success) {
        return response.data.data
      }

      throw new Error(response?.data?.message || 'Failed to load certification progress')
    } catch (error) {
      handleErrors('getTransformCertificationProgress', error)
      throw error
    }
  }

  return {
    getTransformCertificationProgress,
    getAgencyMembers,
    createUser,
    grantUserRoleKey,
    deactivateUser,
    resendInvite,
    cancelInvite,
    getAgencyEmployers,
    updateEmployerAssignments
  }
}
